import React, { useState, useEffect, useMemo } from 'react';
import { FaCalculator } from 'react-icons/fa';
import Loader from '../Common/Loader';
import { useFirebase } from '../Auth/FirebaseProvider';
import { AccountingData, accountingService } from '../../services/accountingService';
import MonthlyOrdersView from './MonthlyOrdersView';
import { Box, Typography } from '@mui/material';
import DateRangePicker from './DateRangePicker';

interface DateRange {
  start: Date;
  end: Date;
}

const AccountingManagement: React.FC = () => {
  const { foodTruckId } = useFirebase();
  const [accountingData, setAccountingData] = useState<AccountingData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange] = useState<DateRange>({
    start: useMemo(() => {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      date.setHours(0, 0, 0, 0);
      return date;
    }, []),
    end: useMemo(() => {
      const date = new Date();
      date.setHours(23, 59, 59, 999);
      return date;
    }, [])
  });

  useEffect(() => {
    if (!foodTruckId) return;

    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true);
        const accountingSrv = accountingService.getInstance(foodTruckId);
        
        const data = await accountingSrv.getAccountingData(dateRange.start, dateRange.end);
        setAccountingData(data);
        setLoading(false);
      } catch (error) {
        console.error("Error loading data:", error);
        setError("Erreur lors du chargement des données");
        setLoading(false);
      }
    };

    fetchData();
  }, [foodTruckId, dateRange]);

  const exportToCSV = (startDate: Date, endDate: Date): void => {
    // First fetch the data for the selected period
    const fetchAndExport = async (): Promise<void> => {
      try {
        setLoading(true);
        const accountingSrv = accountingService.getInstance(foodTruckId);
        const exportData = await accountingSrv.getAccountingData(startDate, endDate);
        
        // Format dates for filename
        const startStr = startDate.toLocaleDateString('fr-FR').replace(/\//g, '-');
        const endStr = endDate.toLocaleDateString('fr-FR').replace(/\//g, '-');
        
        const headers = [
          'Date',
          'Montant Total',
          'Base HT',
          'Quota HT',
          'TVA Base',
          'TVA Quota',
          'ESPÈCES',
          'CB',
          'TICKETS RESTO',
          'APP',
          'AUTRES',
          'Nb Commandes',
          'Nb Clients',
        ];

        const csvContent = [
          headers.join(','),
          ...exportData.map(row => [
            row.date.toDate().toLocaleDateString('fr-FR'),
            row.totalAmount,
            row.totalBase,
            row.totalQuota,
            row.taxBase,
            row.taxQuota,
            row.paymentMethods.cash,
            row.paymentMethods.card,
            row.paymentMethods.ticket,
            row.paymentMethods.app,
            row.paymentMethods.other,
            row.ordersCount,
            row.customersCount,
          ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `rapport_comptabilite_${startStr}_a_${endStr}.csv`;
        link.click();
        
        setLoading(false);
      } catch (error) {
        console.error("Error exporting data:", error);
        setError("Erreur lors de l'exportation des données");
        setLoading(false);
      }
    };

    fetchAndExport();
  };

  if (loading) return <Loader />;
  if (error) return <Box className="p-6 text-red-600">{error}</Box>;

  return (
    <Box sx={{ p: 0 }}>
      {/* Header de la page */}
      <Box sx={{ 
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        p: 3,
        mb: 3
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FaCalculator size={20} color="white" />
              </Box>
              Comptabilité
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary',
                mt: 0.5
              }}
            >
              Gérez votre comptabilité et exportez vos données
            </Typography>
          </Box>
          
          <DateRangePicker onExport={exportToCSV} />
        </Box>
      </Box>

      {/* Contenu principal */}
      <Box sx={{ px: 3 }}>
        <Box sx={{ mt: 2, mb: 4 }}>
          <Typography variant="subtitle1" color="text.secondary">
            Période actuelle: {dateRange.start.toLocaleDateString('fr-FR')} - {dateRange.end.toLocaleDateString('fr-FR')}
          </Typography>
        </Box>

        <MonthlyOrdersView orders={accountingData} />
      </Box>
    </Box>
  );
};

export default AccountingManagement;